import { isTaskShownOnScopeReport, ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { useScanEditingContextSlice, useScanPartNotes, useShowNotes } from '@/Client/State';
import { css } from '@emotion/react';
import { ScanNoteType } from '@kaiizen/kai-pro-api';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../../Styles';

import { SerializedStyles } from '@emotion/react';
import { Button, PhotoDropZone } from '../../Widgets';
import { ExpandIcon } from '../../Widgets/ExpandIcon';
import { InlineTaskEditorDetails } from './InlineTaskEditorDetails';
import { InlineTaskEditorTaskBar } from './InlineTaskEditorTaskBar';

const TASK_ROW_CONTAINER_STYLE = css`
  label: TASK_ROW_CONTAINER_STYLE;
  display: flex;
  cursor: pointer;
  border-bottom: 1px solid var(--border2);
  background: var(--background);
`;

const TASK_COLUMN_CONTAINER_STYLE = css`
  label: TASK_COLUMN_CONTAINER_STYLE;
  display: flex;
  flex-direction: column;
  background: var(--background2);
  flex: 1;
  flex-shrink: 0;
`;

const TASK_NARROW_LEFT_COLUMN_STYLE = css`
  label: TASK_NARROW_LEFT_COLUMN_STYLE;
  width: ${LAYOUT2.spacing * 4}px;
  margin: ${LAYOUT2.spacing}px;
  background: ${COLORS2.alwaysDark.gray};
  flex-shrink: 0;
  transition: background 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.125);
`;

const EXPANDED_EDITS_REQUESTED_LEFT_COLUMN_STYLE = css`
  label: EXPANDED_EDITS_REQUESTED_LEFT_COLUMN_STYLE;
  background: ${COLORS2.alwaysDark.yellow};
`;

const EXPANDED_TASK_NARROW_LEFT_COLUMN_STYLE = css`
  label: SCOPED_TASK_NARROW_LEFT_COLUMN_STYLE;
  background: ${COLORS2.alwaysDark.green};
`;

const COLLAPSED_SCOPED_TASK_NARROW_LEFT_COLUMN_STYLE = css`
  label: COLLAPSED_SCOPED_TASK_NARROW_LEFT_COLUMN_STYLE;
  background: ${COLORS2.alwaysDark.lightGreen};
`;

const COLLAPSED_EDITS_REQUESTED_LEFT_COLUMN_STYLE = css`
  label: COLLAPSED_EDITS_REQUESTED_LEFT_COLUMN_STYLE;
  background: ${COLORS2.alwaysDark.lightYellow};
`;

const LEFT_COLUMN_ICON_CONTAINER_STYLE = css`
  label: LEFT_COLUMN_ICON_CONTAINER_STYLE;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  transition: transform 0.3s ease;
`;

const TASK_REVIEW_SECTION_STYLE = css`
  label: TASK_REVIEW_SECTION_STYLE;
  display: flex;
  flex-direction: column;
  padding: ${LAYOUT2.spacing1}px;
  gap: ${LAYOUT2.spacing1}px;
  background: var(--background2);
  border-left: 1px solid var(--border2);
  min-width: 200px;
`;

const TASK_REVIEW_SECTION_STYLE_INFO = css`
  label: TASK_REVIEW_SECTION_STYLE_INFO;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
  padding: ${LAYOUT2.spacing1}px;
  background: var(--background2);
`;

const TASK_REVIEW_SECTION_APPROVED_STYLE = css`
  label: TASK_REVIEW_SECTION_APPROVED_STYLE;
  background: ${COLORS2.alwaysDark.lightGreen};
`;

const TASK_REVIEW_SECTION_EDIT_REQUESTED_STYLE = css`
  label: TASK_REVIEW_SECTION_EDIT_REQUESTED_STYLE;
  background: ${COLORS2.alwaysDark.lightYellow};
`;

const TaskExpandToggleColumn = (
  { taskExpanded, taskIsScoped, taskIsEditRequested, toggleTaskExpanded }: {
    taskExpanded: boolean;
    taskIsScoped: boolean;
    taskIsEditRequested: boolean;
    toggleTaskExpanded: () => void;
  },
) => {
  return (
    <div
      css={[
        TASK_NARROW_LEFT_COLUMN_STYLE,
        taskExpanded && EXPANDED_TASK_NARROW_LEFT_COLUMN_STYLE,
        !taskExpanded && taskIsScoped && COLLAPSED_SCOPED_TASK_NARROW_LEFT_COLUMN_STYLE,
        taskIsEditRequested && (taskExpanded ? EXPANDED_EDITS_REQUESTED_LEFT_COLUMN_STYLE : COLLAPSED_EDITS_REQUESTED_LEFT_COLUMN_STYLE),
      ]}
      onClick={toggleTaskExpanded}
    >
      <div css={[LEFT_COLUMN_ICON_CONTAINER_STYLE]}>
        <ExpandIcon direction={taskExpanded ? 'down' : 'right'} color={COLORS2.alwaysDark.charcoal} />
      </div>
    </div>
  );
};

export const InlineTaskEditor = React.memo(({
  baselineTask,
  expanded,
  groupId,
  scrollToTopNow,
  setExpandedTaskId,
  specialMode,
  task,
}: {
  baselineTask?: ScanTaskOptimized;
  expanded: boolean;
  groupId: string;
  scrollToTopNow?: boolean;
  setExpandedTaskId: (taskId: string | undefined) => void;
  specialMode?: 'review' | 'changesRequested';
  task: ScanTaskOptimized;
}) => {
  const { showNotesEditor } = useShowNotes();
  const { scanId, scanMutators, scan, approvalStatusByTaskId } = useScanEditingContextSlice(['scanId', 'scanMutators', 'scan', 'approvalStatusByTaskId']);
  const inlineTaskRef = useRef<HTMLDivElement>(null);
  const { scanPartNotes: scanNotes, scanNoteMutators } = useScanPartNotes(
    task?.id,
  );

  const taskIsApproved = approvalStatusByTaskId[task.id] === 'approved';
  const taskIsEditRequested = approvalStatusByTaskId[task.id] === 'editRequested';
  const taskIsEditResolved = approvalStatusByTaskId[task.id] === 'editResolved';

  const [showConfirmOptions, setShowConfirmOptions] = useState(false);

  const taskExpanded = expanded;

  const toggleTaskExpanded = () => {
    setExpandedTaskId(taskExpanded ? undefined : task.id);
  };

  let reviewButtons: ReactNode[] | undefined = undefined;
  let reviewInfo: ReactNode | undefined = undefined;
  let buttonSectionStyle: (SerializedStyles | boolean | undefined | null)[] | undefined = undefined;

  const handleQuickReview = async (noteType: ScanNoteType) => {
    if (!scan) return;
    if (scan.scanLifecycleStatus === 'pendingReview') {
      await scanMutators.update(scan.id, {
        scanLifecycleStatus: 'inReview',
      });
    }
    await scanNoteMutators.create({ scanPartId: task.id, scanNoteType: noteType, body: '' });
    setShowConfirmOptions(false);
  };

  const allowedNoteTypes: ScanNoteType[] = ['info'];
  if (specialMode === 'review') {
    if (!taskIsApproved) allowedNoteTypes.push('approval');
    if (!taskIsEditRequested) allowedNoteTypes.push('editRequest');
    reviewButtons = taskIsApproved || taskIsEditRequested ? ['✓'] : showConfirmOptions
      ? [
        <Button
          key='confirmApproval'
          buttonStyle='green'
          onClick={() => {
            handleQuickReview('approval');
          }}
        >
          Confirm Approval
        </Button>,
        <Button
          key='approveWithNote'
          buttonStyle='green'
          onClick={() => {
            showNotesEditor({ scanPart: task, allowedNoteTypes: ['info', 'approval'] });
            setShowConfirmOptions(false);
          }}
        >
          Approve With Note
        </Button>,
        <Button key='cancel' buttonStyle='gray' onClick={() => setShowConfirmOptions(false)}>cancel</Button>,
      ]
      : [
        <Button key='approve' buttonStyle='green' onClick={() => setShowConfirmOptions(true)}>Approve</Button>,
        <Button key='requestEdits' buttonStyle='yellow' onClick={() => showNotesEditor({ scanPart: task, allowedNoteTypes: ['info', 'editRequest'] })}>
          Request Edits
        </Button>,
      ];
    buttonSectionStyle = [
      taskIsApproved && TASK_REVIEW_SECTION_APPROVED_STYLE,
      taskIsEditRequested && TASK_REVIEW_SECTION_EDIT_REQUESTED_STYLE,
    ];
  } else if (specialMode === 'changesRequested') {
    if (!taskIsApproved) allowedNoteTypes.push('approval');
    if (!taskIsEditResolved) allowedNoteTypes.push('editResolution');
    reviewButtons = showConfirmOptions
      ? [
        <Button
          key='confirmResolved'
          buttonStyle='green'
          onClick={() => {
            handleQuickReview('editResolution');
            setShowConfirmOptions(false);
          }}
        >
          Confirm Resolved
        </Button>,
        <Button
          key='resolvedWithNote'
          buttonStyle='green'
          onClick={() => {
            showNotesEditor({ scanPart: task, allowedNoteTypes: ['info', 'editResolution'] });
            setShowConfirmOptions(false);
          }}
        >
          Resolved With Note
        </Button>,
        <Button key='cancel' buttonStyle='gray' onClick={() => setShowConfirmOptions(false)}>cancel</Button>,
      ]
      : [
        taskIsEditRequested ? <Button key='editsResolved' buttonStyle='green' onClick={() => setShowConfirmOptions(true)}>Edits Resolved</Button> : undefined,
      ];
    if (!taskIsEditRequested && !taskIsEditResolved && !taskIsApproved) {
      reviewInfo = (
        <div css={TASK_REVIEW_SECTION_STYLE_INFO}>
          This task has not been reviewed.
        </div>
      );
    }
    buttonSectionStyle = [
      taskIsEditResolved && TASK_REVIEW_SECTION_APPROVED_STYLE,
    ];
  }

  const scrollToTopOfTask = useCallback(() => {
    inlineTaskRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (scrollToTopNow) scrollToTopOfTask();
  }, [scrollToTopNow, scrollToTopOfTask]);

  const taskIsScoped = isTaskShownOnScopeReport(task);

  return (
    <>
      <a id={`task-${task.id}`} />
      <PhotoDropZone scanPart={task} scanPartContext={{ groupId, taskId: task.id }}>
        <div css={TASK_ROW_CONTAINER_STYLE} ref={inlineTaskRef}>
          <TaskExpandToggleColumn
            taskExpanded={taskExpanded}
            taskIsScoped={taskIsScoped}
            taskIsEditRequested={taskIsEditRequested}
            toggleTaskExpanded={toggleTaskExpanded}
          />

          <div css={[TASK_COLUMN_CONTAINER_STYLE, taskExpanded]}>
            <InlineTaskEditorTaskBar
              allowedNoteTypes={allowedNoteTypes}
              baselineTask={baselineTask}
              expanded={taskExpanded}
              groupId={groupId}
              approvalStatus={approvalStatusByTaskId[task.id]}
              notes={scanNotes}
              task={task}
              toggleInlineTaskEditor={toggleTaskExpanded}
              taskIsEditRequested={taskIsEditRequested}
            />
            {(taskExpanded || taskIsScoped) && (
              <InlineTaskEditorDetails
                taskExpanded={taskExpanded}
                toggleTaskExpanded={toggleTaskExpanded}
                task={task}
                groupId={groupId}
              />
            )}
          </div>
          {reviewButtons && (
            <div css={[TASK_REVIEW_SECTION_STYLE, ...buttonSectionStyle || []]}>
              {reviewButtons}
              {reviewInfo}
            </div>
          )}
        </div>
      </PhotoDropZone>
    </>
  );
});
